import { OptionalArgTuple } from '@deltasierra/type-utilities';
import { IntegrationAuthenticationStatus } from '../status';

export interface IntegrationAuthenticationConfiguration<
    TypeName,
    ConfigurationDataTypeUnion extends {
        type: ConfigurationTypes;
    },
    ConfigurationTypes = ConfigurationDataTypeUnion['type'],
> {
    type: TypeName;

    data: ConfigurationDataTypeUnion;
    status: IntegrationAuthenticationStatus;
}

export interface IntegrationAuthenticationConfigurationConstructor<
    R,
    ConfigurationDataTypeUnion extends {
        type: ConfigurationTypes;
    },
    ConfigurationTypes = ConfigurationDataTypeUnion['type'],
> {
    new (data: ConfigurationDataTypeUnion, status: IntegrationAuthenticationStatus): R;

    of<
        T extends ConfigurationTypes,
        JointType = ConfigurationDataTypeUnion & {
            type: T;
        },
    >(
        type: T,
        status: IntegrationAuthenticationStatus,
        ...data: OptionalArgTuple<keyof Omit<JointType, 'type'> extends never ? undefined : Omit<JointType, 'type'>>
    ): R;
}

export function createIntegrationAuthenticationConfigurationClassMixin<
    ConfigurationDataTypeUnion extends {
        type: ConfigurationTypes;
    },
    TypeName,
    ConfigurationTypes = ConfigurationDataTypeUnion['type'],
>(
    typeName: TypeName,
): IntegrationAuthenticationConfigurationConstructor<
    IntegrationAuthenticationConfiguration<TypeName, ConfigurationDataTypeUnion, ConfigurationTypes>,
    ConfigurationDataTypeUnion,
    ConfigurationTypes
> {
    class IntegrationAuthenticationConfigurationMixin
        implements IntegrationAuthenticationConfiguration<TypeName, ConfigurationDataTypeUnion, ConfigurationTypes> {
        public readonly type: TypeName;

        public constructor(
            public readonly data: ConfigurationDataTypeUnion,
            public readonly status: IntegrationAuthenticationStatus,
        ) {
            this.type = typeName;
        }

        public static of<
            T extends ConfigurationTypes,
            JointType = ConfigurationDataTypeUnion & {
                type: T;
            },
        >(
            type: T,
            status: IntegrationAuthenticationStatus,
            ...data: OptionalArgTuple<keyof Omit<JointType, 'type'> extends never ? undefined : Omit<JointType, 'type'>>
        ): IntegrationAuthenticationConfigurationMixin {
            return new IntegrationAuthenticationConfigurationMixin(
                {

                    ...data?.[0] ?? {},
                    type,
                } as ConfigurationDataTypeUnion & {
                    type: T;
                },
                status,
            );
        }
    }

    return IntegrationAuthenticationConfigurationMixin;
}
